/**
 * Browser launcher — the idle browser panel.
 *
 * Replaces the centered globe and "Enter a URL to load a page" that used to
 * fill this space. That message described the address bar the user was already
 * looking at; this offers the places they would have typed into it.
 *
 * Sections, in order of how likely the user wants them: the app running in this
 * thread, then history. When a project has no server and no history there is
 * nothing to list, so it falls back to a single small empty state rather than
 * two empty headings.
 *
 * @module BrowserLauncher
 */
import { GlobeIcon, HistoryIcon, Loader2Icon, PlayIcon, RadioTowerIcon } from "lucide-react";

import { Empty, EmptyDescription, EmptyMedia, EmptyTitle } from "../../components/ui/empty";
import type { BrowserLauncherSections, BrowserLauncherTarget } from "./browserLauncherLogic";
import { BrowserLauncherRow } from "./BrowserLauncherRow";

export interface BrowserLauncherProps {
  readonly sections: BrowserLauncherSections;
  readonly onOpenUrl: (url: string) => void;
  readonly onRemoveRecent: (url: string) => void;
  /**
   * The thread's app command. Forge can start the server itself, which is the
   * one thing a browser's new-tab page normally cannot do for you — so it
   * belongs here rather than only in a hint strip above the panel.
   *
   * `actionLabel` comes from the same view model the strip card uses ("Launch
   * app" / "Launch again" / "Open app"), because the handler behind it is the
   * same and the three cases are not interchangeable to a reader.
   */
  readonly launchAction?:
    | {
        readonly label: string;
        readonly detail: string;
        readonly actionLabel: string;
        readonly busy: boolean;
        readonly onLaunch: () => void;
      }
    | undefined;
}

export function BrowserLauncher(props: BrowserLauncherProps) {
  if (!props.sections.hasTargets && props.launchAction === undefined) {
    return <BrowserLauncherEmpty />;
  }

  return (
    <div className="flex h-full min-h-0 overflow-y-auto px-4 py-6">
      <div className="mx-auto flex w-full max-w-lg flex-col gap-5">
        {props.launchAction === undefined ? null : (
          <BrowserLauncherLaunchCard action={props.launchAction} />
        )}
        {props.sections.live.length === 0 ? null : (
          <BrowserLauncherSection
            icon={<RadioTowerIcon className="size-3.5 shrink-0" />}
            title="Live in this thread"
          >
            {props.sections.live.map((target) => (
              <BrowserLauncherRow
                key={`${target.kind}:${target.url}`}
                target={target}
                onOpen={() => props.onOpenUrl(target.url)}
              />
            ))}
          </BrowserLauncherSection>
        )}
        {props.sections.recents.length === 0 ? null : (
          <BrowserLauncherSection
            icon={<HistoryIcon className="size-3.5 shrink-0" />}
            title="Recently used"
          >
            {props.sections.recents.map((target: BrowserLauncherTarget) => (
              <BrowserLauncherRow
                key={`${target.kind}:${target.url}`}
                target={target}
                onOpen={() => props.onOpenUrl(target.url)}
                onRemove={() => props.onRemoveRecent(target.url)}
              />
            ))}
          </BrowserLauncherSection>
        )}
      </div>
    </div>
  );
}

function BrowserLauncherSection(props: {
  readonly icon: React.ReactNode;
  readonly title: string;
  readonly children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="flex items-center gap-1.5 px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {props.icon}
        {props.title}
      </h3>
      <div className="flex flex-col divide-y divide-border/60 overflow-hidden rounded-lg border border-border/70 bg-background">
        {props.children}
      </div>
    </section>
  );
}

function BrowserLauncherLaunchCard(props: {
  readonly action: NonNullable<BrowserLauncherProps["launchAction"]>;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border/70 bg-background p-3">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm font-medium text-foreground">{props.action.label}</span>
        <span className="truncate font-mono text-xs text-muted-foreground">
          {props.action.detail}
        </span>
      </div>
      {/*
       * The label stays put while busy — only the glyph spins. Swapping it for
       * "Starting…" would change the button's accessible name mid-flight, so a
       * screen-reader user (and any test) would lose the control it was on.
       */}
      <button
        type="button"
        className="inline-flex h-7 w-fit items-center gap-1.5 rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50"
        disabled={props.action.busy}
        onClick={props.action.onLaunch}
      >
        {props.action.busy ? (
          <Loader2Icon className="size-3.5 animate-spin" />
        ) : (
          <PlayIcon className="size-3.5" />
        )}
        {props.action.actionLabel}
      </button>
    </div>
  );
}

function BrowserLauncherEmpty() {
  return (
    <Empty>
      <EmptyMedia variant="icon">
        <GlobeIcon className="size-4.5 text-muted-foreground" />
      </EmptyMedia>
      <EmptyTitle>No page open</EmptyTitle>
      <EmptyDescription>
        Type a URL above, or start this thread&apos;s app. Live servers and pages you have opened
        show up here.
      </EmptyDescription>
    </Empty>
  );
}
